import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router';
import { loadMe } from '../lib/me';
import { AppLayout } from '../shell/AppLayout';
import { PortalAppLayout } from '../shell/PortalLayout';
import { NotFoundPage, RootErrorPage } from '../shell/RootPages';

/**
 * Parent routes that page modules attach to (import from here, never from router/index.tsx):
 *
 *   createRoute({ getParentRoute: () => appRoute, path: '/matters', component: MattersPage })
 */

export type RouterContext = { queryClient: QueryClient };

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: RootErrorPage,
  notFoundComponent: NotFoundPage,
});

/** Public pages (marketing, auth, intake, booking, pay, portal entry): no shell, no guard. */
export const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: Outlet,
});

/** Signed-in area: redirects to /login when there is no session. */
export const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } });
    return { me };
  },
  component: Outlet,
});

/** The app shell (top nav). Owners who have not finished onboarding are sent to the wizard. */
export const appRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: 'app',
  beforeLoad: ({ context }) => {
    const me = (context as { me?: { role: string; settings: { onboardingCompleted: boolean } } }).me;
    if (me && me.role === 'owner' && !me.settings.onboardingCompleted) throw redirect({ to: '/onboarding' });
  },
  component: AppLayout,
});

/** Client portal (signed in with the lb_portal cookie). */
export const portalAppRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/app',
  component: PortalAppLayout,
});
